// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import type { AutodeskUrn } from '@evolvemep/api-ts';
import { Box, Button, Card, CloseButton, Flex, Indicator, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AutoDimensioningPanelSection } from 'components/Autodesk/extensions/DimensioningExtension/AutoDimensioningPanelSection';
import type { RenderedTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createRenderedTagsStore';
import type { TagInteractionCallbacks } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/TagLabel';
import { TagLabelsOverlay } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/TagLabelsOverlay';
import { useElementTagsPersistence } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/useElementTagsPersistence';
import type { RenderedRackLabelsStore } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/createRenderedRackLabelsStore';
import { RackDimensionLabelsOverlay } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/RackDimensionLabelsOverlay';
import { useDimensionableElements } from 'components/Autodesk/extensions/DimensioningExtension/useDimensionableElements';
import {
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';

import { ElementTagsPanelSection } from './ElementTags/ElementTagsPanelSection';

type Props = {
  renderedTagsStore: RenderedTagsStore;
  renderedRackLabelsStore: RenderedRackLabelsStore;
  tagCallbacks: TagInteractionCallbacks;
  getUrn: () => AutodeskUrn | null;
};

/**
 * Floating in-viewer UI for element tags and automatic conduit-rack dimensions. Mounts directly inside
 * the viewer container so it can position tag and dimension labels over the canvas without a
 * DockingPanel.
 */
export const DimensioningPanel = ({ renderedTagsStore, renderedRackLabelsStore, tagCallbacks, getUrn }: Props) => {
  useDimensionableElements();

  const [isOpen, { open, close }] = useDisclosure(false);
  const { isDirty, tagsVisible } = useAutodeskExtensionStoreSnapshot('elementTags');
  const { setTagsVisible } = useAutodeskExtensionStoreActions('elementTags');
  const { dimensionsVisible } = useAutodeskExtensionStoreSnapshot('dimensioning');
  const { setRackDimensionsVisible } = useAutodeskExtensionStoreActions('dimensioning');

  // Make tags visible whenever the panel is opened.
  useEffect(() => {
    if (isOpen) setTagsVisible(true);
  }, [isOpen, setTagsVisible]);

  useElementTagsPersistence(getUrn());

  const theme = useMantineTheme();

  const allVisible = tagsVisible && dimensionsVisible;

  return (
    <>
      <Box pos="absolute" style={{ top: 10, left: 10, zIndex: 1 }}>
        {isOpen ? (
          <Card withBorder p="xs" pb={0} w={350} bg="gray.1">
            <Flex justify="space-between" align="center">
              <Title order={5}>Annotate</Title>
              <CloseButton onClick={close} aria-label="Close annotations panel" />
            </Flex>
            <AutoDimensioningPanelSection />
            <ElementTagsPanelSection />
          </Card>
        ) : (
          <Indicator disabled={!isDirty} processing color="yellow" position="top-start">
            <Button.Group style={{ gap: '1px' }}>
              <AgGridStyleTooltip
                label={
                  isDirty ? 'You have unsaved changes. Click to open the panel and save them.' : 'Open annotate panel'
                }
                openDelay={isDirty ? 0 : undefined}
                withArrow
              >
                <Button
                  leftSection={<EvolveIcon icon="Label" size="sm" />}
                  onClick={open}
                  variant="gradient"
                  gradient={{ from: `${theme.primaryColor}.6`, to: `${theme.primaryColor}.5` }}
                >
                  Annotate
                </Button>
              </AgGridStyleTooltip>
              <AgGridStyleTooltip
                label={
                  allVisible
                    ? 'Annotations shown. Click to hide annotations'
                    : tagsVisible || dimensionsVisible
                      ? 'Some types of annotations are hidden. Click to show all annotations.'
                      : 'Annotations hidden. Click to show annotations'
                }
                withArrow
              >
                <Button
                  size="input-sm"
                  color={`${theme.primaryColor}.5`}
                  variant="filled"
                  onClick={() => {
                    setRackDimensionsVisible(!allVisible);
                    setTagsVisible(!allVisible);
                  }}
                  px="xs"
                >
                  <EvolveIcon icon={allVisible ? 'Visible' : 'Hidden'} />
                </Button>
              </AgGridStyleTooltip>
            </Button.Group>
          </Indicator>
        )}
      </Box>

      <RackDimensionLabelsOverlay renderedRackLabelsStore={renderedRackLabelsStore} />
      <TagLabelsOverlay renderedTagsStore={renderedTagsStore} callbacks={tagCallbacks} />
    </>
  );
};
